// src/pages/DetalleConsumoCliente.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Chip, TablePagination, // <-- Importamos TablePagination
  Card, CardContent // Importaciones añadidas para el estilo de la respuesta anterior
} from '@mui/material';
import api from '../api/api';

const DetalleConsumoCliente = ({ idCliente }) => {
  const [consumos, setConsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ESTADOS DE PAGINACIÓN
  const [page, setPage] = useState(0); 
  const [rowsPerPage, setRowsPerPage] = useState(10); // Fijo a 10 filas por página

  useEffect(() => {
    const fetchConsumo = async () => {
      setLoading(true); // Se agrega el set loading aquí para más robustez
      try {
        const res = await api.get(`/documentos_fiscales/consumo-cliente/${idCliente}`);
        setConsumos(res.data);
        setPage(0); // Reiniciar la página a 0 cuando los datos cambian
      } catch (err) {
        console.error("Error cargando consumo", err);
      } finally {
        setLoading(false);
      }
    };
    if (idCliente) fetchConsumo();
  }, [idCliente]);

  // FUNCIONES DE PAGINACIÓN
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const formatCurrency = (val) => new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(val || 0);

  // LÓGICA DE CORTE DE DATOS PARA LA PÁGINA ACTUAL
  const paginatedConsumos = consumos
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ mt: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          📊 Consumo del cliente (ítems facturados)
        </Typography>

        {consumos.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="textSecondary" variant="subtitle1">
              No hay consumos registrados aún.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="medium" aria-label="Consumo del cliente" sx={{ minWidth: 1000 }}>
                <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                  <TableRow>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Factura</strong></TableCell>
                    <TableCell><strong>Receptor DTE</strong></TableCell>
                    <TableCell><strong>Paciente</strong></TableCell>
                    <TableCell><strong>Servicio</strong></TableCell>
                    <TableCell align="right"><strong>Cant.</strong></TableCell>
                    <TableCell align="right"><strong>Precio Uni</strong></TableCell>
                    <TableCell align="right"><strong>Tributos</strong></TableCell>
                    <TableCell align="right"><strong>IVA (Monto)</strong></TableCell>
                    <TableCell align="right"><strong>Total Ítem</strong></TableCell>
                  </TableRow>
                </TableHead>
                
                <TableBody>
                  {/* Usamos los datos paginados */}
                  {paginatedConsumos.map((item, i) => ( 
                    <TableRow 
                      key={item.id_item_factura || i} 
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover } }}
                    >
                      <TableCell>{new Date(item.fecha).toLocaleDateString()}</TableCell>
                      <TableCell>{item.factura}</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>{item.receptor_factura}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{item.paciente || '—'}</TableCell>
                      <TableCell>{item.descripcion}</TableCell>
                      <TableCell align="right">{item.cantidad}</TableCell>
                      <TableCell align="right">{formatCurrency(item.precio_uni)}</TableCell>
                      
                      <TableCell>
                          <Chip 
                              label={item.tributos} 
                              size="small" 
                              color={item.tributos.includes("IVA") ? "primary" : "default"}
                          />
                      </TableCell>

                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {item.iva_item > 0 ? formatCurrency(item.iva_item) : "—"}
                      </TableCell>
                      
                      <TableCell align="right">
                        <Chip 
                          label={formatCurrency(item.total_item)} 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* COMPONENTE DE PAGINACIÓN */}
            <TablePagination
              rowsPerPageOptions={[10]} // Solo mostramos 10 por opción
              component="div"
              count={consumos.length} // Total de ítems
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              // Como rowsPerPage es fijo a 10, no necesitamos onRowsPerPageChange
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DetalleConsumoCliente;